import { useId, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  CONSENT_VERSIONS,
  CV_UPLOAD,
  FORM_ID,
  PRIVACY_NOTICE,
  PRIVACY_NOTICE_VERSION,
} from "@/config/contact-form";
import { trackFormError, trackFormSubmit } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";
import { submitToForms4Love, type Forms4Field } from "@/lib/forms4love";
import { ExpandingSection } from "./ExpandingSection";

type FormType = "candidate" | "client";
type FormStep = "path" | "step1" | "step2";
type Status = "idle" | "loading" | "success" | "error";

const inputCls =
  "w-full bg-black/40 border border-white/15 px-3.5 py-3 text-sm text-white focus:border-lime-300 outline-none transition-colors placeholder:text-white/30";
const labelCls =
  "block font-mono text-[10px] uppercase tracking-[0.28em] text-white/60 mb-1.5";
const errorCls = "mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-red-300";
const helpCls = "mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-white/40";

const REQUIRED_MARK = <span className="text-lime-300" aria-hidden>*</span>;

function isEmail(v: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

export default function ContactForm() {
  const { t } = useTranslation();
  const formId = useId();
  const rodoId = useId();

  const [formType, setFormType] = useState<FormType | null>(null);
  const [step, setStep] = useState<FormStep>("path");
  const [rodoOpen, setRodoOpen] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hp, setHp] = useState("");

  const submittingRef = useRef(false);

  // Candidate fields
  const [cName, setCName] = useState("");
  const [cEmail, setCEmail] = useState("");
  const [cPhone, setCPhone] = useState("");
  const [cLinkedin, setCLinkedin] = useState("");
  const [cRole, setCRole] = useState("");
  const [cMessage, setCMessage] = useState("");
  const [cFile, setCFile] = useState<File | null>(null);
  const [cFutureRecruitment, setCFutureRecruitment] = useState(false);
  const [cAck, setCAck] = useState(false);

  // Client fields
  const [bName, setBName] = useState("");
  const [bCompany, setBCompany] = useState("");
  const [bEmail, setBEmail] = useState("");
  const [bPhone, setBPhone] = useState("");
  const [bRoles, setBRoles] = useState("");
  const [bVolume, setBVolume] = useState("");
  const [bMessage, setBMessage] = useState("");
  const [bMarketing, setBMarketing] = useState(false);
  const [bAck, setBAck] = useState(false);

  const ctaLabel = formType === "candidate" ? t("form.sendApplication") : t("form.sendEnquiry");

  const supportingCopy = useMemo(() => {
    if (formType === "candidate") return t("form.candidateHint");
    if (formType === "client") return t("form.clientHint");
    return t("form.choosePath");
  }, [formType, t]);

  function choosePath(tp: FormType) {
    setFormType(tp);
    setStep("step1");
    setErrors({});
  }

  function goToStep1() {
    setStep("step1");
    setErrors({});
  }

  function validateFile(file: File | null): string | null {
    if (!file) return null;
    if (!(CV_UPLOAD.acceptedMime as readonly string[]).includes(file.type)) {
      return t("form.errors.fileFormat", { accepted: CV_UPLOAD.acceptedLabel });
    }
    if (file.size > CV_UPLOAD.maxSizeMB * 1024 * 1024) {
      return t("form.errors.fileSize", { max: CV_UPLOAD.maxSizeMB });
    }
    return null;
  }

  function onFileChange(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    const err = validateFile(f);
    if (err) {
      setErrors((p) => ({ ...p, cv: err }));
      setCFile(null);
      e.target.value = "";
      return;
    }
    setErrors((p) => { const { cv, ...rest } = p; return rest; });
    setCFile(f);
  }

  function validateStep1(): boolean {
    const e: Record<string, string> = {};
    const req = t("form.errors.required");
    const validEmail = t("form.errors.validEmail");
    if (formType === "candidate") {
      if (!cName.trim()) e.cName = req;
      if (!isEmail(cEmail)) e.cEmail = validEmail;
    } else if (formType === "client") {
      if (!bName.trim()) e.bName = req;
      if (!bCompany.trim()) e.bCompany = req;
      if (!isEmail(bEmail)) e.bEmail = validEmail;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function goToStep2() {
    if (validateStep1()) {
      setStep("step2");
      setErrors({});
    }
  }

  function validate(): boolean {
    const e: Record<string, string> = {};
    const req = t("form.errors.required");
    const ackReq = t("form.errors.ackRequired");
    if (formType === "candidate") {
      if (!cRole.trim()) e.cRole = req;
      const fileErr = validateFile(cFile);
      if (fileErr) e.cv = fileErr;
      if (!cAck) e.cAck = ackReq;
    } else if (formType === "client") {
      if (!bRoles.trim()) e.bRoles = req;
      if (!bMessage.trim()) e.bMessage = req;
      if (!bAck) e.bAck = ackReq;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function onSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    if (submittingRef.current) return;
    if (hp) return;
    if (!formType || step !== "step2") return;
    if (!validate()) return;

    submittingRef.current = true;
    setStatus("loading");

    const basePayload = {
      formId: FORM_ID,
      formType,
      privacyNoticeVersion: PRIVACY_NOTICE_VERSION,
      checkboxVersions: CONSENT_VERSIONS,
      submittedAt: new Date().toISOString(),
      sourcePage: typeof window !== "undefined" ? window.location.href : "",
      locale: typeof navigator !== "undefined" ? navigator.language : "en",
    };

    const payload =
      formType === "candidate"
        ? {
            ...basePayload,
            data: {
              name: cName.trim(),
              email: cEmail.trim(),
              phone: cPhone.trim() || null,
              linkedin: cLinkedin.trim() || null,
              role: cRole.trim(),
              message: cMessage.trim() || null,
              cvFileName: cFile?.name ?? null,
              cvFileSize: cFile?.size ?? null,
            },
            consents: {
              privacyAcknowledgement: cAck,
              futureRecruitmentConsent: cFutureRecruitment,
            },
          }
        : {
            ...basePayload,
            data: {
              name: bName.trim(),
              company: bCompany.trim(),
              email: bEmail.trim(),
              phone: bPhone.trim() || null,
              roles: bRoles.trim(),
              hiringVolume: bVolume.trim() || null,
              message: bMessage.trim(),
            },
            consents: {
              privacyAcknowledgement: bAck,
              marketingConsent: bMarketing,
            },
          };

    try {
      const submissionId = crypto.randomUUID();
      const data = payload.data as Record<string, any>;
      const consents = (payload as any).consents as Record<string, boolean>;
      const recipientEmail = formType === "candidate" ? cEmail.trim() : bEmail.trim();
      const recipientName = formType === "candidate" ? cName.trim() : bName.trim();

      // Visitors can upload but not read CVs, so no signed URL here — recruiters open it from storage.
      const cvDownloadUrl: string | null = null;
      let cvPath: string | null = null;
      if (formType === "candidate" && cFile) {
        const safeName = cFile.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const path = `submissions/${submissionId}/${safeName}`;
        const { error: upErr } = await supabase.storage
          .from("contact-cvs")
          .upload(path, cFile, { contentType: cFile.type, upsert: false });
        if (upErr) throw upErr;
        cvPath = path;
      }

      const { error: insErr } = await supabase.from("contact_submissions").insert({
        id: submissionId,
        form_type: formType,
        name: recipientName,
        email: recipientEmail,
        phone: data.phone ?? null,
        company: data.company ?? null,
        linkedin: data.linkedin ?? null,
        role: (data.role ?? data.roles) || null,
        message: data.message ?? null,
        cv_path: cvPath,
        consents,
        source_page: payload.sourcePage.slice(0, 1000),
      });
      if (insErr) throw insErr;

      try {
        const f4Fields: Forms4Field[] =
          formType === "candidate"
            ? [
                { name: "Full name", value: recipientName, type: "name" },
                { name: "E-mail", value: recipientEmail, type: "email" },
                { name: "Phone", value: data.phone ?? "", type: "phone" },
                { name: "LinkedIn", value: data.linkedin ?? "", type: "other" },
                { name: "Role of interest", value: data.role ?? "", type: "other" },
                { name: "Message", value: data.message ?? "", type: "message" },
                { name: "CV file name", value: data.cvFileName ?? "", type: "other" },
                { name: "CV download URL", value: cvDownloadUrl ?? "", type: "other" },
                { name: "Future recruitment consent", value: consents.futureRecruitmentConsent ? "yes" : "no", type: "other" },
                { name: "Form type", value: "candidate", type: "other" },
                { name: "Source page", value: payload.sourcePage, type: "other" },
              ]
            : [
                { name: "Full name", value: recipientName, type: "name" },
                { name: "Company", value: data.company ?? "", type: "company" },
                { name: "E-mail", value: recipientEmail, type: "email" },
                { name: "Phone", value: data.phone ?? "", type: "phone" },
                { name: "Roles to fill", value: data.roles ?? "", type: "other" },
                { name: "Hiring volume", value: data.hiringVolume ?? "", type: "other" },
                { name: "Message", value: data.message ?? "", type: "message" },
                { name: "Marketing consent", value: consents.marketingConsent ? "yes" : "no", type: "other" },
                { name: "Form type", value: "client", type: "other" },
                { name: "Source page", value: payload.sourcePage, type: "other" },
              ];
        const f4Res = await submitToForms4Love(
          f4Fields.filter((f) => f.value !== ""),
          submissionId
        );
        if (f4Res.status !== "ok" && f4Res.status !== "duplicate_submission") {
          if (import.meta.env.DEV) console.warn("[FORMS4.love]", f4Res);
        }
      } catch (f4Err) {
        if (import.meta.env.DEV) console.warn("[FORMS4.love] submit failed", f4Err);
      }

      const { error: emailErr } = await supabase.functions.invoke("contact-form-email", {
        body: {
          submissionId,
          formType,
          name: recipientName,
          email: recipientEmail,
          phone: data.phone,
          company: data.company,
          linkedin: data.linkedin,
          role: data.role,
          roles: data.roles,
          hiringVolume: data.hiringVolume,
          message: data.message,
          cvFileName: data.cvFileName,
          consents,
          sourcePage: payload.sourcePage,
          submittedAt: payload.submittedAt,
          sendConfirmation: true,
        },
      });
      if (emailErr && import.meta.env.DEV) console.warn("[ContactForm] email failed", emailErr);

      setStatus("success");
      trackFormSubmit("contact", { form_type: formType, source_page: payload.sourcePage });
    } catch (err) {
      if (import.meta.env.DEV) console.error("[ContactForm] submit failed", err);
      trackFormError("contact", err instanceof Error ? err.message : "unknown");
      setStatus("error");
    } finally {
      submittingRef.current = false;
    }
  }

  if (status === "success") {
    return (
      <div className="bg-[#0a0d0b] border border-black/20 shadow-[0_24px_70px_-24px_rgba(0,0,0,0.7)] p-8 md:p-10 flex flex-col gap-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-lime-200">{t("form.successEyebrow")}</p>
        <h3 className="h-mega text-3xl text-white">{t("form.successTitle")}</h3>
        <p className="text-body text-sm leading-relaxed">{t("form.successBody")}</p>
        <button
          type="button"
          onClick={() => { setStatus("idle"); setFormType(null); setStep("path"); }}
          className="font-mono text-[10px] uppercase tracking-[0.28em] text-lime-300 hover:underline w-fit"
        >
          {t("form.sendAnother")}
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="bg-[#0a0d0b] border border-black/20 shadow-[0_24px_70px_-24px_rgba(0,0,0,0.7)] p-8 md:p-10 flex flex-col gap-7"
      aria-describedby={`${formId}-notice`}
    >
      {/* Honeypot */}
      <div aria-hidden style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
        <label htmlFor={`${formId}-website`}>Website</label>
        <input id={`${formId}-website`} type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} />
      </div>

      {/* Path selector */}
      <fieldset>
        <legend className={labelCls}>{t("form.iAmA")}</legend>
        <div role="tablist" aria-label={t("form.iAmA")} className="grid grid-cols-2 gap-2">
          {(["candidate", "client"] as const).map((tp) => {
            const active = formType === tp;
            return (
              <button
                key={tp}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => choosePath(tp)}
                className={`px-4 py-3.5 font-mono text-[11px] uppercase tracking-[0.24em] border transition-colors focus-visible:ring-2 focus-visible:ring-lime-300 focus-visible:ring-offset-2 focus-visible:ring-offset-black ${
                  active
                    ? "bg-lime-300 text-black border-lime-300"
                    : "bg-black/40 text-white/70 border-white/15 hover:border-lime-300/60 hover:text-white"
                }`}
              >
                {tp === "candidate" ? t("form.candidate") : t("form.client")}
              </button>
            );
          })}
        </div>
      </fieldset>

      {/* Step progress - visible in step1 and step2 */}
      {step !== "path" && (
        <div>
          <div className="flex gap-1.5 mb-2.5">
            <div className="flex-1 h-[2px] bg-lime-300" />
            <div
              className={`flex-1 h-[2px] transition-colors duration-500 ${
                step === "step2" ? "bg-lime-300" : "bg-white/20"
              }`}
            />
          </div>
          <div className="flex justify-between">
            <span
              className={`font-mono text-[10px] uppercase tracking-[0.28em] transition-colors ${
                step === "step1" ? "text-lime-300" : "text-white/40"
              }`}
            >
              {t("form.stepInfo")}
            </span>
            <span
              className={`font-mono text-[10px] uppercase tracking-[0.28em] transition-colors ${
                step === "step2" ? "text-lime-300" : "text-white/30"
              }`}
            >
              {t("form.stepDetails")}
            </span>
          </div>
        </div>
      )}

      {/* Path hint */}
      {step === "path" && (
        <p className="text-body text-sm leading-relaxed text-white/60">{supportingCopy}</p>
      )}

      {/* ── STEP 1 - CANDIDATE ── */}
      {step === "step1" && formType === "candidate" && (
        <div className="space-y-5">
          <p className="text-body text-sm leading-relaxed text-white/60">{t("form.candidateHint")}</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field id={`${formId}-c-name`} label={<>{t("form.fullName")} {REQUIRED_MARK}</>} error={errors.cName}>
              <input id={`${formId}-c-name`} type="text" autoComplete="name" value={cName} onChange={(e) => setCName(e.target.value)} className={inputCls} required />
            </Field>
            <Field id={`${formId}-c-email`} label={<>{t("form.email")} {REQUIRED_MARK}</>} error={errors.cEmail}>
              <input id={`${formId}-c-email`} type="email" autoComplete="email" value={cEmail} onChange={(e) => setCEmail(e.target.value)} className={inputCls} required />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field id={`${formId}-c-phone`} label={t("form.phoneOptional")}>
              <input id={`${formId}-c-phone`} type="tel" autoComplete="tel" value={cPhone} onChange={(e) => setCPhone(e.target.value)} className={inputCls} />
            </Field>
            <Field id={`${formId}-c-linkedin`} label={t("form.linkedinOptional")}>
              <input id={`${formId}-c-linkedin`} type="url" placeholder="https://linkedin.com/in/…" value={cLinkedin} onChange={(e) => setCLinkedin(e.target.value)} className={inputCls} />
            </Field>
          </div>
          <button
            type="button"
            onClick={goToStep2}
            className="w-full font-bold uppercase tracking-widest text-xs py-4 bg-lime-300 text-black hover:-translate-y-0.5 transition-transform"
          >
            {t("form.nextStep")}
          </button>
        </div>
      )}

      {/* ── STEP 1 - CLIENT ── */}
      {step === "step1" && formType === "client" && (
        <div className="space-y-5">
          <p className="text-body text-sm leading-relaxed text-white/60">{t("form.clientHint")}</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field id={`${formId}-b-name`} label={<>{t("form.fullName")} {REQUIRED_MARK}</>} error={errors.bName}>
              <input id={`${formId}-b-name`} type="text" autoComplete="name" value={bName} onChange={(e) => setBName(e.target.value)} className={inputCls} required />
            </Field>
            <Field id={`${formId}-b-company`} label={<>{t("form.company")} {REQUIRED_MARK}</>} error={errors.bCompany}>
              <input id={`${formId}-b-company`} type="text" autoComplete="organization" value={bCompany} onChange={(e) => setBCompany(e.target.value)} className={inputCls} required />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field id={`${formId}-b-email`} label={<>{t("form.workEmail")} {REQUIRED_MARK}</>} error={errors.bEmail}>
              <input id={`${formId}-b-email`} type="email" autoComplete="email" value={bEmail} onChange={(e) => setBEmail(e.target.value)} className={inputCls} required />
            </Field>
            <Field id={`${formId}-b-phone`} label={t("form.phoneOptional")}>
              <input id={`${formId}-b-phone`} type="tel" autoComplete="tel" value={bPhone} onChange={(e) => setBPhone(e.target.value)} className={inputCls} />
            </Field>
          </div>
          <button
            type="button"
            onClick={goToStep2}
            className="w-full font-bold uppercase tracking-widest text-xs py-4 bg-lime-300 text-black hover:-translate-y-0.5 transition-transform"
          >
            {t("form.nextStep")}
          </button>
        </div>
      )}

      {/* ── STEP 2 - CANDIDATE ── */}
      {step === "step2" && formType === "candidate" && (
        <div className="space-y-5">
          <Field id={`${formId}-c-role`} label={<>{t("form.roleInterest")} {REQUIRED_MARK}</>} error={errors.cRole}>
            <input id={`${formId}-c-role`} type="text" value={cRole} onChange={(e) => setCRole(e.target.value)} className={inputCls} required />
          </Field>
          <Field id={`${formId}-c-msg`} label={t("form.messageOptional")}>
            <textarea id={`${formId}-c-msg`} rows={4} value={cMessage} onChange={(e) => setCMessage(e.target.value)} className={`${inputCls} resize-none`} placeholder={t("form.candidateMsgPlaceholder")} />
          </Field>
          {CV_UPLOAD.enabled && (
            <Field id={`${formId}-c-cv`} label={t("form.cv")} error={errors.cv}>
              <div className="flex items-center gap-4 flex-wrap">
                <label htmlFor={`${formId}-c-cv`} className="inline-flex items-center justify-center px-4 py-2 bg-lime-300 text-black font-bold uppercase tracking-widest text-[10px] cursor-pointer hover:bg-lime-200 transition-colors">
                  {t("form.chooseFile")}
                </label>
                <span className="text-xs text-white/70 truncate">{cFile ? cFile.name : t("form.noFile")}</span>
                <input id={`${formId}-c-cv`} type="file" accept={CV_UPLOAD.acceptedMime.join(",")} onChange={onFileChange} className="sr-only" />
              </div>
              <p className={helpCls}>{t("form.cvHelp", { accepted: CV_UPLOAD.acceptedLabel, max: CV_UPLOAD.maxSizeMB })}</p>
            </Field>
          )}
          <div className="space-y-3 pt-1">
            <ConsentCheckbox
              id={`${formId}-c-ack`}
              checked={cAck}
              onChange={setCAck}
              error={errors.cAck}
              required
              label={
                <Trans i18nKey="form.ackPrivacy" components={[<Link key="0" to="/privacy-policy" className="underline decoration-white/30 hover:decoration-lime-300" />]} />
              }
            />
            <ConsentCheckbox
              id={`${formId}-c-future`}
              checked={cFutureRecruitment}
              onChange={setCFutureRecruitment}
              label={t("form.futureRecruitment")}
            />
          </div>
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={goToStep1}
              className="font-mono text-[11px] uppercase tracking-[0.24em] border border-white/20 text-white/60 px-5 py-4 hover:border-white/40 hover:text-white transition-colors"
            >
              {t("form.prevStep")}
            </button>
            <button
              type="submit"
              disabled={status === "loading"}
              className="flex-1 font-bold uppercase tracking-widest text-xs py-4 bg-lime-300 text-black hover:-translate-y-0.5 transition-transform disabled:bg-white/10 disabled:text-white/40 disabled:hover:translate-y-0 disabled:cursor-not-allowed"
            >
              {status === "loading" ? t("form.sending") : t("form.sendApplication")}
            </button>
          </div>
          {status === "error" && (
            <p role="alert" className="text-xs text-red-300 font-mono uppercase tracking-[0.18em]">
              {t("form.errorRetry")}
            </p>
          )}
          <p className="text-center font-mono text-[10px] uppercase tracking-[0.28em] text-white/45">
            {t("form.replyTime")}
          </p>
        </div>
      )}

      {/* ── STEP 2 - CLIENT ── */}
      {step === "step2" && formType === "client" && (
        <div className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field id={`${formId}-b-roles`} label={<>{t("form.rolesToFill")} {REQUIRED_MARK}</>} error={errors.bRoles}>
              <input id={`${formId}-b-roles`} type="text" value={bRoles} onChange={(e) => setBRoles(e.target.value)} className={inputCls} placeholder={t("form.rolesPlaceholder")} required />
            </Field>
            <Field id={`${formId}-b-vol`} label={t("form.hiringVolume")}>
              <input id={`${formId}-b-vol`} type="text" value={bVolume} onChange={(e) => setBVolume(e.target.value)} className={inputCls} />
            </Field>
          </div>
          <Field id={`${formId}-b-msg`} label={<>{t("form.messageRequired")} {REQUIRED_MARK}</>} error={errors.bMessage}>
            <textarea id={`${formId}-b-msg`} rows={4} value={bMessage} onChange={(e) => setBMessage(e.target.value)} className={`${inputCls} resize-none`} placeholder={t("form.clientMsgPlaceholder")} required />
          </Field>
          <div className="space-y-3 pt-1">
            <ConsentCheckbox
              id={`${formId}-b-ack`}
              checked={bAck}
              onChange={setBAck}
              error={errors.bAck}
              required
              label={
                <Trans i18nKey="form.ackPrivacy" components={[<Link key="0" to="/privacy-policy" className="underline decoration-white/30 hover:decoration-lime-300" />]} />
              }
            />
            <ConsentCheckbox
              id={`${formId}-b-mkt`}
              checked={bMarketing}
              onChange={setBMarketing}
              label={t("form.marketing")}
            />
          </div>
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={goToStep1}
              className="font-mono text-[11px] uppercase tracking-[0.24em] border border-white/20 text-white/60 px-5 py-4 hover:border-white/40 hover:text-white transition-colors"
            >
              {t("form.prevStep")}
            </button>
            <button
              type="submit"
              disabled={status === "loading"}
              className="flex-1 font-bold uppercase tracking-widest text-xs py-4 bg-lime-300 text-black hover:-translate-y-0.5 transition-transform disabled:bg-white/10 disabled:text-white/40 disabled:hover:translate-y-0 disabled:cursor-not-allowed"
            >
              {status === "loading" ? t("form.sending") : t("form.sendEnquiry")}
            </button>
          </div>
          {status === "error" && (
            <p role="alert" className="text-xs text-red-300 font-mono uppercase tracking-[0.18em]">
              {t("form.errorRetry")}
            </p>
          )}
          <p className="text-center font-mono text-[10px] uppercase tracking-[0.28em] text-white/45">
            {t("form.replyTime")}
          </p>
        </div>
      )}

      {/* RODO footer - always visible */}
      <div id={`${formId}-notice`} className="pt-5 border-t border-white/10 mt-auto">
        <button
          type="button"
          aria-expanded={rodoOpen}
          aria-controls={rodoId}
          onClick={() => setRodoOpen((v) => !v)}
          className="text-left text-[11px] leading-relaxed text-white/50 hover:text-white/70 transition-colors"
        >
          {PRIVACY_NOTICE.controller} - {t("form.rodoToggle")} {rodoOpen ? "−" : "+"}
        </button>
        <ExpandingSection open={rodoOpen}>
          <div id={rodoId} className="space-y-2 pt-2">
            <p className="text-[11px] leading-relaxed text-white/50">
              {t("form.noticeShort", { controller: PRIVACY_NOTICE.controller })}
            </p>
            <p className="text-[11px] leading-relaxed text-white/50">
              {t("form.noticeFull")}{" "}
              <Link className="underline hover:text-lime-300" to="/privacy-policy">
                {t("form.policyLink")}
              </Link>
            </p>
          </div>
        </ExpandingSection>
      </div>
    </form>
  );
}

function Field({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: React.ReactNode;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className={labelCls}>
        {label}
      </label>
      {children}
      {error && (
        <p className={errorCls} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function ConsentCheckbox({
  id,
  checked,
  onChange,
  label,
  error,
  required,
}: {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  label: React.ReactNode;
  error?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className="flex items-start gap-3 cursor-pointer text-[11px] leading-relaxed text-white/70">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-lime-300 shrink-0"
          required={required}
        />
        <span>
          {label} {required && REQUIRED_MARK}
        </span>
      </label>
      {error && (
        <p className={errorCls} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
